import { Ref, useCallback, useEffect, useRef, useState } from "react";

import { useRefinementList } from "react-instantsearch";

interface SteelcaseRefinementListProps {
    filterName: string;
    filterAttr: string;
    count: number;
    limit?: number;
    searchable?: boolean;
    placeholderText?: string;
    sort?: string[];
    pinnedItems?: string[];
    sortItems?: string[];
    sortItemCriteria?: string[];
    showHeading?: boolean;
    setApplyCount: (filterAttr: string, filterCount:number) => void;
    isMobile: boolean;
}

type SortByDirection<TCriterion extends string> = TCriterion | `${TCriterion}:asc` | `${TCriterion}:desc`;

type RefinementListItem = {
    value: string;
    label: string;
    highlighted?: string;
    count: number;
    isRefined: boolean;
}

export default function SteelcaseRefinementList({ filterName, filterAttr, count, showHeading = true, limit = 10, sortItems, sortItemCriteria, placeholderText, searchable, setApplyCount, pinnedItems, sort, isMobile }: SteelcaseRefinementListProps){
    const [checkedAttrs, setCheckedAttrs] = useState<{ [key: string]: boolean}>({});
    const [checkedItemsCountSum, setCheckedItemsCountSum] = useState(0);

    const transformItemsCallback = useCallback((items: RefinementListItem[]) => {

            if (sortItems && sortItems.length > 0) {
                const pinnedRefinements: RefinementListItem[] = []
                if (pinnedItems && pinnedItems.length > 0) {
                    pinnedItems
                        .filter(value => items.some(item => item.value === value))
                        .forEach(value => {
                            const itemToPin = items.find(item => item.value === value) as RefinementListItem;
                            pinnedRefinements.push(itemToPin);
                        });
                }

                const sortItemsToBucket: { [key: string]: RefinementListItem[] } = {};
                sortItems.forEach((sortItem) => {
                    sortItemsToBucket[sortItem] = items.filter(item => item.value.startsWith(sortItem) && !pinnedRefinements.includes(item));
                });

                const processItems = (refinementList: RefinementListItem[], sortItemsCriteria: string[]): RefinementListItem[] => {
                    if (sortItemsCriteria === null || sortItemsCriteria === undefined || sortItemsCriteria.length === 0) return refinementList;

                    sortItemsCriteria.forEach(criteria => {
                        const countSortDescFn = (a: RefinementListItem, b: RefinementListItem) => b.count - a.count;
                        const countSortAscFn = (a: RefinementListItem, b: RefinementListItem) => a.count - b.count;

                        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
                        const endsWithLetter = (str: string): boolean => {
                            if (str.length === 0) return false
                            
                            const lastChar = str.charAt(str.length - 1);
                            const code = lastChar.charCodeAt(0);

                            if (code >= 65 && code <= 90) return true;
                            
                            if (code >= 97 && code <= 122) return true;

                            return false;
                        }

                        const getNumberSuffix = (str: string): number => {
                            const match = str.match(/(\d+)$/);
                            return match ? parseInt(match[1], 10) : NaN;
                        }

                        // Custom sorting based on Marketing Defined Requirements, we prioritize items that end with a letter.
                        const isNameSortAscFn = (a: RefinementListItem, b: RefinementListItem) => {
                            const aEndsWithLetter = endsWithLetter(a.label);
                            const bEndsWithLetter = endsWithLetter(b.label);

                            const aNum = getNumberSuffix(a.label);
                            const bNum = getNumberSuffix(b.label);

                            if (aEndsWithLetter && !bEndsWithLetter) return -1;
                            if (!aEndsWithLetter && bEndsWithLetter) return 1;
                            
                            if (!isNaN(aNum) && !isNaN(bNum)) {
                                return aNum - bNum;
                            }

                            if (!isNaN(aNum)) return 1;
                            if (!isNaN(bNum)) return -1;

                            return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
                        };

                        const isNameSortDescFn = (a: RefinementListItem, b: RefinementListItem) => {

                            const aEndsWithLetter = endsWithLetter(a.label);
                            const bEndsWithLetter = endsWithLetter(b.label);

                            const aNum = getNumberSuffix(a.label);
                            const bNum = getNumberSuffix(b.label);

                            if (aEndsWithLetter && !bEndsWithLetter) return -1;
                            if (!aEndsWithLetter && bEndsWithLetter) return 1;

                            if (!isNaN(aNum) && !isNaN(bNum)) {
                                return bNum - aNum;
                            }

                            if (!isNaN(aNum)) return 1;
                            if (!isNaN(bNum)) return -1;

                            return b.label.localeCompare(a.label, undefined, { sensitivity: 'base' })
                        };

                        if (criteria === "count:desc") refinementList.sort(countSortDescFn);
                        if (criteria === "count:asc") refinementList.sort(countSortAscFn);
                        if (criteria === "name:desc") refinementList.sort(isNameSortDescFn);
                        if (criteria === "name:asc") refinementList.sort(isNameSortAscFn);
                    });

                    return refinementList;
                }

                const sortedGroupedItems = sortItems.flatMap(sortItem => {
                    const group = sortItemsToBucket[sortItem] || [];
                    if (sortItemCriteria) {
                        return processItems([...group], sortItemCriteria);
                    }
                    return [...group]
                })

                const sortedGroupValues = new Set(sortedGroupedItems.map(item => item.value));
                const remainingItems = items.filter(item => !sortedGroupValues.has(item.value) && !pinnedRefinements.includes(item));
                const finalItems = [...sortedGroupedItems, ...remainingItems.sort((a, b) => b.count - a.count)];

                const isRefinedSortFn = (a: RefinementListItem, b: RefinementListItem) => a.isRefined ? -1 : 1;

                return [...pinnedRefinements, ...finalItems]
                        .sort(isRefinedSortFn);
            }

            if(pinnedItems && pinnedItems.length > 0) {
                const remainingItems = items.filter(item => !pinnedItems.includes(item.value));
                const pinnedRefinements = pinnedItems
                    .filter(value => items.some(item => item.value === value))
                    .map(value => items.find(item => item.value === value) as RefinementListItem);
                
                return [...pinnedRefinements, ...remainingItems];
            }

            return items;
        }, [pinnedItems, sortItems, sortItemCriteria]);

    const refinementListApi = useRefinementList({
        attribute: filterAttr,
        limit: 100,
        sortBy: sort ? sort.map((criterion): SortByDirection<'count' | 'name' | 'isRefined'> => {
            const [value, direction] = criterion.split(':');
            return `${value}:${direction}` as SortByDirection<'count' | 'name' | 'isRefined'>; 
        }) as SortByDirection<'count' | 'name'| 'isRefined'>[] :  ["isRefined", "count:desc", "name:asc"],
        transformItems: transformItemsCallback
    });
    
    const firstRun = useRef(true);

    useEffect(() => {
        const initialState = Object.fromEntries(refinementListApi.items.map(item => item.isRefined ? [item.value, true] : [item.value, false]));
        setCheckedAttrs(initialState);
    }, [refinementListApi.items])

    useEffect(() => {
        if(firstRun.current) {
            firstRun.current = false;
            return;
        }
        
        ApplyFilters();
    }, [count])


    useEffect(() => {
        
        const sum = Object.entries(checkedAttrs).reduce((total, [value, isChecked]) => {
        const item = refinementListApi.items.find(item => item.value === value);
        return isChecked && item ? total + item.count : total;
        }, 0);

        setCheckedItemsCountSum(sum);
    }, [checkedAttrs, refinementListApi.items]); 

    useEffect(() => {
        setApplyCount(filterAttr, checkedItemsCountSum)
    }, [checkedItemsCountSum])

    const ApplyFilters = () => {
        const currState: { [key: string]: boolean} = Object.fromEntries(refinementListApi.items.map((item) =>
            [item.value, item.isRefined]
        ))

        Object.entries(checkedAttrs).map(([key, value]) => {
            if(!currState[key] && value){
                refinementListApi.refine(key);
            }
            if(currState[key] && !value){
                refinementListApi.refine(key);
            }
        })

    }

    const handleCheckboxClick = (value: string, count: number) => {
        setCheckedAttrs((prevCheckedAttrs) => {
            const newCheckedAttrs = { ...prevCheckedAttrs };
            newCheckedAttrs[value] = !newCheckedAttrs[value];
            return newCheckedAttrs;
        }) 
    }

    return (
        <>
            {
                showHeading && 
                    <h3 className={"finish-filters-dropdown__heading"}>{filterName}</h3>
            }
            {
                searchable && 
                    <div className={"finish-search-box__container finish-details__container--col-span-all"}>
                        <div>
                            <button type="submit" className="finish-search-box__submit">
                            </button>
                        </div>
                        <div className={"finish-search-box__input--wrapper"}>
                            <input
                                type="search"
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                                maxLength={512}
                                className={"finish-search-box__input"}
                                placeholder={placeholderText ?? ""}
                                onChange={(event) => refinementListApi.searchForItems(event.currentTarget.value)}
                            />
                        </div>
                    </div>
            }
            {
                refinementListApi.items.slice(0, limit).map((item, idx) => {
                    const id = `${filterName}${item.label}${idx}`;
                    return (
                        <div className={"finish-filters-dropdown__options-grid"} key={`${item.label}-${idx}`}>
                            <input 
                                type="checkbox" 
                                autoComplete="off" 
                                id={isMobile ? `${id}-mobile` : id} 
                                checked={checkedAttrs[item.value] || false}
                                onChange={() => {
                                    handleCheckboxClick(item.value, item.count);
                                }}
                            />
                            <label className={"finish-filters-dropdown__label"} htmlFor={isMobile ? `${id}-mobile` : id}>
                                <span>{item.label}</span>
                                <span className={"finish-filters-dropdown__label-results"}>{item.count}</span>
                            </label>
                        </div>
                    )
                })
            }
        </>
    )
}

